import { SortByMethod, TestCollection } from './parser/models/testCollection.js';
import { TestParserOptions } from './options/testParserOptions.js';
import { parseObjectFields } from './parser/parseObjectFields.js';
import { executePostProcessing } from './processors/executePostProcessing.js';
import { executePreProcessing } from './processors/executePreProcessing.js';

/**
 * Represents a module to be parsed for tests.
 * Typically a record of exported functions, classes, or objects.
 */
export type TestModule = Record<string, any>

/**
 * Extracts tests from a given module based on the provided options.
 * 
 * This function orchestrates the full parsing pipeline:
 * 1. Parses raw object fields from the module.
 * 2. Executes pre-processing to identify initial test structures.
 * 3. Executes post-processing to expand test cases and apply filters.
 * 4. Optionally sorts the resulting test collection.
 * 
 * @param moduleToParse - The JavaScript module or object containing test definitions.
 * @param options - Configuration options for the test parser.
 * @returns A {@link TestCollection} containing the extracted and processed tests.
 */
export function extractTestsFromModule(moduleToParse: TestModule, options: Partial<TestParserOptions>): TestCollection {
  const parserOptions = new TestParserOptions(options);

  // pre process module fields
  const fields = parseObjectFields("root", moduleToParse);
  const tests = executePreProcessing(fields, parserOptions);

  // post process tests
  const testCollection = executePostProcessing(
    moduleToParse,
    tests,
    parserOptions
  );

  // get the sort method
  const sortMethod = SortByMethod[parserOptions.sort!]

  // return sorted collection
  return sortMethod
    ? new TestCollection(testCollection.sortBy(sortMethod))
    : testCollection;
}
